import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to calculate the available vertical scroll height for Ant Design tables.
 * It listens for window and container resizes to keep the table height responsive.
 * Optionally deducts a custom delta to account for elements below the table.
 *
 * @param {number} delta - The amount of space (in px) to subtract from the available height (e.g. padding, footers). Defaults to 70.
 * @param {boolean} isTableVisible - When true, the scroll container is mounted (table or grid view).
 * @param {string} containerSelector - DOM selector for the element whose `top` anchors the available height.
 * @returns {{ tableScrollY: number }} - The calculated vertical scroll height for the table (in px).
 */
function useTableHeight(
  delta = 70,
  isTableVisible = true,
  containerSelector = '.ant-table-wrapper',
) {
  const [tableScrollY, setTableScrollY] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const observedElementRef = useRef<Element | null>(null);

  /**
   * Computes and sets the available height for the table container.
   * It looks for the first Ant Design table wrapper in the DOM.
   */
  const updateTableHeight = () => {
    const scrollContainer = document.querySelector(containerSelector);
    if (scrollContainer) {
      const offsetTop = scrollContainer.getBoundingClientRect().top;
      const availableHeight = window.innerHeight - offsetTop - delta;
      setTableScrollY(Math.max(0, availableHeight));
    }
  };

  // Re-run when table visibility changes (e.g. switch from grid to list) so we measure after the table is mounted
  useEffect(() => {
    if (!isTableVisible) {
      setTableScrollY(0);
      return;
    }

    // Defer until after the table has been committed and laid out (double rAF)
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updateTableHeight();
      });
    });
    return () => cancelAnimationFrame(id);
  }, [isTableVisible, delta, containerSelector]);

  useEffect(() => {
    window.addEventListener('resize', updateTableHeight);

    // When table becomes visible, set up ResizeObserver on the next frame (table might not exist yet)
    const timeoutId = setTimeout(() => {
      const scrollContainer = document.querySelector(containerSelector);
      if (scrollContainer && isTableVisible) {
        observedElementRef.current = scrollContainer;
        observerRef.current = new ResizeObserver(() => {
          updateTableHeight();
        });
        observerRef.current.observe(scrollContainer);
      }
    }, 0);

    return () => {
      window.removeEventListener('resize', updateTableHeight);
      clearTimeout(timeoutId);
      if (observerRef.current && observedElementRef.current) {
        observerRef.current.unobserve(observedElementRef.current);
        observerRef.current = null;
        observedElementRef.current = null;
      }
    };
  }, [isTableVisible, containerSelector]);

  return {
    tableScrollY,
  };
}

export default useTableHeight;
